// Copyright ©️ 2026 eVolve MEP, LLC

import type { StrongOmit, WithRequired } from '../_helpers/helper-types';

import type { DateOnly } from '../Misc/dateOnly';

declare const customPropertyIdSymbol: unique symbol;
export type CustomPropertyId = string & { [customPropertyIdSymbol]: never };

declare const customPropertyTypeIdSymbol: unique symbol;
export type CustomPropertyTypeId = string & { [customPropertyTypeIdSymbol]: never };

export const customPropertyTypes = {
  'Work Order': '700cec9c-7509-4987-ac1b-f7b26b96ab0b' as CustomPropertyTypeId,
  Task: 'd354a0e6-8ec6-49a1-82b1-27d2d2355440' as CustomPropertyTypeId,
  Shipment: '08472ed6-6714-4786-b063-44f712f1c493' as CustomPropertyTypeId,
  'Fabrication Item': 'f7f19173-b1a8-45ba-a0e5-7a1d2e66e353' as CustomPropertyTypeId,
} as const satisfies Record<string, CustomPropertyTypeId>;
export type CustomPropertyTypeName = keyof typeof customPropertyTypes;

export type CustomPropertyDataTypeMap = {
  number: number;
  text: string;
  date: DateOnly;
  toggle: boolean;
  dropdown: CustomPropertyOptionId;
};

export type CustomProperty = {
  customPropertyId: CustomPropertyId;
  customPropertyTypeId: CustomPropertyTypeId;
  name: string;
  description?: string;
  dataType: keyof CustomPropertyDataTypeMap;
  /** Only populated if `dataType == 'dropdown'` */
  options?: WithRequired<CustomPropertyOption, 'customPropertyOptionId'>[];
};

declare const customPropertyOptionIdSymbol: unique symbol;
export type CustomPropertyOptionId = string & { [customPropertyOptionIdSymbol]: never };

export type CustomPropertyOption = {
  customPropertyOptionId?: CustomPropertyOptionId;
  name: string;
};

export type AssignedCustomProperty = {
  [K in keyof CustomPropertyDataTypeMap]: {
    customPropertyId: CustomPropertyId;
    dataType: K;
    value: CustomPropertyDataTypeMap[K] | null;
  };
}[keyof CustomPropertyDataTypeMap];

export type UpsertCustomProperty = StrongOmit<AssignedCustomProperty, 'dataType'>;

export type CreateCustomPropertyBody = {
  customPropertyTypeId: CustomPropertyTypeId;
  dataType: keyof CustomPropertyDataTypeMap;
  name: string;
  description?: string | null;
  options?: CustomPropertyOption[];
};

export type UpdateCustomPropertyBody = StrongOmit<CreateCustomPropertyBody, 'dataType' | 'customPropertyTypeId'>;
